import React, { useState, useRef, useCallback, useEffect } from 'react';

const SIGNATURE_TYPES = [
  { value: 'aadhaar_single', label: 'Aadhaar eSign', desc: 'Single signer — Aadhaar OTP' },
  { value: 'aadhaar_multi', label: 'Aadhaar Multi-Sign', desc: 'Multiple signers — Aadhaar OTP' },
  { value: 'digisign', label: 'Digital Signature', desc: 'DSC-based digital signature' },
];

const EMPTY_SIGNER = { name: '', email: '', mobile: '', sequence: 1 };

const ConfigForm = ({ initialData, onComplete }) => {
  const [file, setFile] = useState(initialData.file || null);
  const [fileBase64, setFileBase64] = useState(initialData.fileBase64 || '');
  const [fileName, setFileName] = useState(initialData.fileName || '');
  const [signatureType, setSignatureType] = useState(initialData.signatureType || 'aadhaar_single');
  const [signers, setSigners] = useState(
    initialData.signers?.length ? initialData.signers : [{ ...EMPTY_SIGNER }]
  );
  const [reason, setReason] = useState(initialData.reason || 'Signing');
  const [daysToExpire, setDaysToExpire] = useState(initialData.daysToExpire || 7);
  const [needNameMatch, setNeedNameMatch] = useState(initialData.needNameMatch || false);
  const [percentageNameMatch, setPercentageNameMatch] = useState(initialData.percentageNameMatch || 80);
  const [redirectUrl, setRedirectUrl] = useState(initialData.redirectUrl || '');
  const [signatureMode, setSignatureMode] = useState(initialData.signatureMode || 'email');
  const [signatureFlow, setSignatureFlow] = useState(initialData.signatureFlow || '1');
  const [notifySignerOnComplete, setNotifySignerOnComplete] = useState(
    initialData.notifySignerOnComplete ?? true
  );
  const [notifyRequesterOnComplete, setNotifyRequesterOnComplete] = useState(
    initialData.notifyRequesterOnComplete ?? true
  );
  const [notifyRequesterOnProgress, setNotifyRequesterOnProgress] = useState(
    initialData.notifyRequesterOnProgress ?? true
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Saved signers state
  const [savedSigners, setSavedSigners] = useState([]);
  const [saveFlags, setSaveFlags] = useState(
    initialData.saveFlags || signers.map(() => false)
  );
  const [searchTerms, setSearchTerms] = useState(signers.map(() => ''));
  const [activeDropdown, setActiveDropdown] = useState(-1);

  const fileInputRef = useRef(null);

  const isMulti = signatureType === 'aadhaar_multi' || signatureType === 'digisign';

  // ── File handling ──────────────────────────────
  const processFile = useCallback((f) => {
    if (!f || f.type !== 'application/pdf') return;
    setFile(f);
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // strip data:...;base64, prefix
      setFileBase64(base64);
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    processFile(f);
  }, [processFile]);

  const handleFileInput = useCallback((e) => {
    processFile(e.target.files[0]);
  }, [processFile]);

  const removeFile = useCallback(() => {
    setFile(null);
    setFileBase64('');
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── Fetch saved signers on mount ────────────────
  useEffect(() => {
    const fetchSavedSigners = async () => {
      try {
        const resp = await fetch('/api/external/esign/signers', {
          credentials: 'include',
        });
        if (resp.ok) {
          const data = await resp.json();
          setSavedSigners(data.signers || []);
        }
      } catch (err) {
        // Silent fail — saved signers is a convenience feature
      }
    };
    fetchSavedSigners();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(-1);
    if (activeDropdown >= 0) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  // ── Saved signer helpers ──────────────────────
  const handleSelectSavedSigner = async (signerIdx, saved) => {
    updateSigner(signerIdx, 'name', saved.name);
    updateSigner(signerIdx, 'email', saved.email);
    updateSigner(signerIdx, 'mobile', saved.mobile || '');
    setActiveDropdown(-1);
    setSearchTerms(prev => prev.map((t, i) => i === signerIdx ? '' : t));

    // Bump use_count in background
    try {
      await fetch(`/api/external/esign/signers/${saved.id}/use`, {
        method: 'PATCH',
        credentials: 'include',
      });
    } catch (e) { /* silent */ }
  };

  const handleDeleteSavedSigner = async (e, signerId) => {
    e.stopPropagation();
    try {
      const resp = await fetch(`/api/external/esign/signers/${signerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (resp.ok) {
        setSavedSigners(prev => prev.filter(s => s.id !== signerId));
      }
    } catch (err) { /* silent */ }
  };

  const toggleSaveFlag = (idx) => {
    setSaveFlags(prev => prev.map((f, i) => i === idx ? !f : f));
  };

  const getFilteredSigners = (idx) => {
    const term = searchTerms[idx]?.toLowerCase() || '';
    if (!term) return savedSigners;
    return savedSigners.filter(
      s => s.name.toLowerCase().includes(term) || s.email.toLowerCase().includes(term)
    );
  };

  // ── Signer management ─────────────────────────
  const updateSigner = useCallback((index, field, value) => {
    setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }, []);

  const addSigner = useCallback(() => {
    setSigners((prev) => [...prev, { ...EMPTY_SIGNER, sequence: prev.length + 1 }]);
    setSaveFlags(prev => [...prev, false]);
    setSearchTerms(prev => [...prev, '']);
  }, []);

  const removeSigner = useCallback((index) => {
    setSigners((prev) =>
      prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, sequence: i + 1 }))
    );
    setSaveFlags(prev => prev.filter((_, i) => i !== index));
    setSearchTerms(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ── Signature type change ─────────────────────
  const handleTypeChange = useCallback((type) => {
    setSignatureType(type);
    if (type === 'aadhaar_single') {
      setSigners([{ ...EMPTY_SIGNER }]);
    }
  }, []);

  // ── Validation ────────────────────────────────
  const isSignerValid = (s) => {
    if (!s.name.trim() || !s.email.trim()) return false;
    if (signatureType !== 'digisign' && !s.mobile.trim()) return false;
    return true;
  };

  const isFormValid = fileBase64 && signers.length > 0 && signers.every(isSignerValid);

  // ── Submit ────────────────────────────────────
  const handleNext = () => {
    if (!isFormValid) return;
    onComplete({
      file,
      fileBase64,
      fileName,
      signatureType,
      signers,
      reason,
      daysToExpire,
      needNameMatch,
      percentageNameMatch,
      redirectUrl,
      signatureMode,
      signatureFlow,
      notifySignerOnComplete,
      notifyRequesterOnComplete,
      notifyRequesterOnProgress,
      saveFlags,
    });
  };

  return (
    <div className="space-y-6">
      {/* ── PDF Upload ──────────────────────────── */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Document</label>
        {!fileBase64 ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
            }`}
          >
            <svg className="w-10 h-10 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600 font-medium">Drag & drop a PDF here, or click to browse</p>
            <p className="text-xs text-gray-400 mt-1">Only .pdf files are accepted</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileInput}
              className="hidden"
            />
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{fileName}</p>
              <p className="text-xs text-gray-500">{(file?.size / 1024).toFixed(1)} KB</p>
            </div>
            <button
              onClick={removeFile}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* ── Signature Type ──────────────────────── */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Signature Type</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {SIGNATURE_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => handleTypeChange(t.value)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                signatureType === t.value
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`text-sm font-medium ${signatureType === t.value ? 'text-indigo-700' : 'text-gray-700'}`}>
                {t.label}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ── DigiSign Options ────────────────────── */}
      {signatureType === 'digisign' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Signature Mode</label>
            <select
              value={signatureMode}
              onChange={(e) => setSignatureMode(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              <option value="email">Email Notification</option>
              <option value="signing_url">Signing URL</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Signing Flow</label>
            <select
              value={signatureFlow}
              onChange={(e) => setSignatureFlow(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              <option value="1">Parallel (all at once)</option>
              <option value="2">Sequential (in order)</option>
            </select>
          </div>
        </div>
      )}

      {/* ── Signer Details ──────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700">
            {isMulti ? 'Signers' : 'Signer Details'}
          </label>
          {isMulti && (
            <button
              type="button"
              onClick={addSigner}
              className="text-xs font-medium text-indigo-700 hover:text-indigo-700 flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
              </svg>
              Add Signer
            </button>
          )}
        </div>

        <div className="space-y-3">
          {signers.map((signer, idx) => (
            <div
              key={idx}
              className={`p-4 rounded-lg border border-gray-200 ${isMulti ? 'bg-gray-50' : ''}`}
            >
              {isMulti && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Signer {idx + 1}
                  </span>
                  {signers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSigner(idx)}
                      className="text-xs text-red-500 hover:text-red-600 font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
              {/* Saved signer dropdown */}
              {savedSigners.length > 0 && (
                <div className="mb-3 relative" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={searchTerms[idx] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSearchTerms(prev => prev.map((t, i) => i === idx ? val : t));
                        setActiveDropdown(idx);
                      }}
                      onFocus={() => setActiveDropdown(idx)}
                      placeholder="Search saved signers..."
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 bg-gray-50"
                    />
                    {activeDropdown === idx && (
                      <button
                        type="button"
                        onClick={() => setActiveDropdown(-1)}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Close
                      </button>
                    )}
                  </div>

                  {activeDropdown === idx && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {getFilteredSigners(idx).length === 0 ? (
                        <p className="px-3 py-2 text-xs text-gray-400">No saved signers found</p>
                      ) : (
                        getFilteredSigners(idx).map(saved => (
                          <div
                            key={saved.id}
                            onClick={() => handleSelectSavedSigner(idx, saved)}
                            className="flex items-center justify-between px-3 py-2 hover:bg-indigo-50 cursor-pointer group"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{saved.name}</p>
                              <p className="text-xs text-gray-400 truncate">
                                {saved.email}{saved.mobile ? ` | ${saved.mobile}` : ''}
                                {saved.use_count > 0 && (
                                  <span className="ml-2 text-indigo-500">Used {saved.use_count}x</span>
                                )}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteSavedSigner(e, saved.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500 transition-opacity"
                              title="Remove saved signer"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={signer.name}
                    onChange={(e) => updateSigner(idx, 'name', e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                  <input
                    type="email"
                    value={signer.email}
                    onChange={(e) => updateSigner(idx, 'email', e.target.value)}
                    placeholder="john@example.com"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Mobile {signatureType !== 'digisign' ? '*' : ''}
                  </label>
                  <input
                    type="tel"
                    value={signer.mobile}
                    onChange={(e) => updateSigner(idx, 'mobile', e.target.value)}
                    placeholder="9876543210"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                </div>
              </div>

              {/* Save signer checkbox */}
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveFlags[idx] || false}
                  onChange={() => toggleSaveFlag(idx)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-600"
                />
                <span className="text-xs text-gray-500">Save signer for future use</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* ── Advanced Options (Accordion) ─────────── */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">Advanced Options</span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAdvanced && (
          <div className="p-4 space-y-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Days to Expire</label>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={daysToExpire}
                  onChange={(e) => setDaysToExpire(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Redirect URL</label>
              <input
                type="url"
                value={redirectUrl}
                onChange={(e) => setRedirectUrl(e.target.value)}
                placeholder="https://example.com/done"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
            </div>

            {signatureType !== 'digisign' && (
              <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                {/* Toggle row */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Name Matching</p>
                    <p className="text-xs text-gray-400">Match signer name against Aadhaar</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNeedNameMatch(!needNameMatch)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      needNameMatch ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        needNameMatch ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Percentage row — shown when enabled */}
                {needNameMatch && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-medium text-gray-600">Match Threshold</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={50}
                          max={100}
                          value={percentageNameMatch}
                          onChange={(e) => setPercentageNameMatch(Number(e.target.value))}
                          className="w-16 px-2 py-1 text-sm border border-gray-200 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        />
                        <span className="text-sm font-medium text-gray-500">%</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 max-w-[200px] text-right">
                      Signer's name must match their Aadhaar name by at least this percentage to proceed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── Notification Preferences ── */}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">
                Email Notifications
              </p>

              {/* Toggle: Notify signer on completion */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm text-gray-700">Notify signer after signing</span>
                  <p className="text-xs text-gray-400">Send confirmation email to signer when they complete</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifySignerOnComplete(!notifySignerOnComplete)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    notifySignerOnComplete ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      notifySignerOnComplete ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Toggle: Notify requester on completion */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm text-gray-700">Notify me when signing completes</span>
                  <p className="text-xs text-gray-400">Receive email when all signatures are collected</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotifyRequesterOnComplete(!notifyRequesterOnComplete)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    notifyRequesterOnComplete ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      notifyRequesterOnComplete ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Toggle: Progress updates (only visible for multi-signer) */}
              {(signatureType === 'aadhaar_multi' || signatureType === 'digisign') && signers.length > 1 && (
                <div className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-sm text-gray-700">Progress updates</span>
                    <p className="text-xs text-gray-400">Email me after each signer completes (multi-signer)</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNotifyRequesterOnProgress(!notifyRequesterOnProgress)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                      notifyRequesterOnProgress ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                        notifyRequesterOnProgress ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Next Button ──────────────────────────── */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleNext}
          disabled={!isFormValid}
          className="px-6 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Next: Place Signatures
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ConfigForm;
